import { Inject, Injectable } from '@angular/core';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateService } from '@ngx-translate/core';
import { IAMService, PersonaType, SortDirection, User, UserSortField } from '@vendasta/iam';
import { Observable, map, of, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { ASSIGNED_FULFILLMENT_AGENT, getFormattedDisplayName } from './users.service';

@Injectable({ providedIn: 'root' })
export class FulfillmentAgentSelectorService implements AutomationReplacementChipsDataService<User> {
  supportsSearching = true;

  private assignedFulfillmentAgent = new User({
    userId: ASSIGNED_FULFILLMENT_AGENT,
    firstName: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CREATE_FULFILLMENT_TASK.ASSIGNED_DIGITAL_AGENT'),
    lastName: '',
  });

  constructor(
    private readonly iamService: IAMService,
    private readonly translateService: TranslateService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {}

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    shouldNotAddAdditionalAssignees: boolean,
  ): Observable<PagedResponse<User>> {
    return this.namespace$.pipe(
      switchMap((namespace) =>
        this.iamService.listUsers(
          namespace,
          undefined,
          cursor,
          pageSize,
          [PersonaType.digital_agent],
          [searchTerm],
          [
            {
              direction: SortDirection.SORT_DIRECTION_ASCENDING,
              field: UserSortField.USER_SORT_FIELD_FIRST_NAME,
            },
            {
              direction: SortDirection.SORT_DIRECTION_ASCENDING,
              field: UserSortField.USER_SORT_FIELD_LAST_NAME,
            },
          ],
        ),
      ),
      take(1),
      map((resp) => {
        const users = resp.users || [];
        if (!cursor && !shouldNotAddAdditionalAssignees) {
          users.unshift(this.assignedFulfillmentAgent);
        }
        return {
          results: users,
          hasMore: resp.hasMore,
          nextCursor: resp.nextCursor,
        };
      }),
    );
  }

  convertDataToDisplay(s: User): Observable<string> {
    return of(getFormattedDisplayName(s) || '');
  }

  convertDataToFormValue(x: User): any {
    return x.userId;
  }

  getMulti(userIds: string[]): Observable<User[]> {
    const assignedFullfilmentAgent = (userIds || []).indexOf(ASSIGNED_FULFILLMENT_AGENT);
    return this.iamService.getMultiUsers(userIds?.map((u) => ({ userId: u }))).pipe(
      map((users) => {
        if (assignedFullfilmentAgent >= 0) {
          users[assignedFullfilmentAgent] = this.assignedFulfillmentAgent;
        }
        return users;
      }),
    );
  }
}
